/**
 * Go Client Brand Preview URL
 *
 * Builds a link to the real Toptracer Go client showing a site's *draft* brand.
 * `?brand=draft` is already read by the Go client and forwarded to its
 * site-config request, so this works against the client as deployed — no change
 * to it is required, and no coupling is introduced beyond a query parameter it
 * already honours.
 *
 * Deliberately adds nothing else. In particular no preview or opt-in parameter:
 * that belongs to the deferred embedded-preview design, and the Go client's query
 * sanitiser strips keys it does not recognise, so including one would be both
 * meaningless and a misleading hint that something else was coming.
 *
 * Requirements: 10.1, 10.5
 */

export interface PreviewUrlParams {
  /** Base URL of the Go client, from runtime config. */
  readonly appHost: string;
  readonly siteId: string;
  /**
   * Optional bay, for views only reachable with one — notably the splash screen
   * that carries `splashLogo`. Omitted means the bay-selector view.
   */
  readonly bayId?: string;
}

/** Build the preview URL for a site's draft brand. */
export function buildPreviewUrl({
  appHost,
  siteId,
  bayId,
}: PreviewUrlParams): string {
  const params = new URLSearchParams({ siteId, brand: 'draft' });

  if (bayId !== undefined && bayId.length > 0) {
    params.set('bayId', bayId);
  }

  // Trailing slashes are a plausible hand-editing mistake in config, and
  // `host//?x` is not the same URL.
  const base = appHost.replace(/\/+$/, '');

  return `${base}/?${params.toString()}`;
}
